import React from "react";
import {
  Stepper,
  Step,
  StepLabel,
  Button,
  Box,
  Dialog,
  Paper,
  DialogTitle,
  Typography,
  Alert,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AddReceiptsReviewTable from "./AddReceiptsReviewTable";
import AddReceiptsMethodSelection from "./AddReceiptsMethodSelection";
import AddReceiptsUploadFile from "./AddReceiptsUploadFile";
import AddReceiptsManualInput from "./AddReceiptsManualInput";
import { ReceiptsEndPointLogic } from "../../../../../logic/Finance/ProviderExpense/ReceiptsEndpointLogic";
import { useSelector } from "react-redux";
import {
  selectUserData,
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { CircularProgress } from "@mui/material";
import {
  resetReceiptsForm,
  setFailedReceipts,
  setFailedReceiptsPayload,
  setFailedReceiptStatus,
  setManualReceiptsPayload,
  setUploadReceiptsPayload,
} from "../../../../../features/financeReceiptSlice";
import { useDispatch } from "react-redux";
import ModalSkeleton from "../helpers/components/ModalSkeleton";
import { ParingRateQueryID } from "../helpers/funcs/staticQID";
import { AddReceiptFailedReport } from "./AddReceiptFailedReport";
import FailedReceipts from "./FailedReceipts";
import dayjs from "dayjs";
import store from "../../../../../store";

interface AddReceiptsProps {
  open: boolean;
  handleClose: () => void;
}

const tableHeadings = {
  company: "Company",
  receipt_number: "Receipt Number",
  receipt_amount: "Receipt Amount",
  finance_company_id: "Finance Company ID",
  fme_record_id: "FME Record ID",
};

const AddReceiptsModal = ({ open, handleClose }: AddReceiptsProps) => {
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const [uploadedFiles, setUploadedFiles] = React.useState<any[]>([]);
  const [parsedRows, setParsedRows] = React.useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [saveSuccess, setSaveSuccess] = React.useState(false);
  const [has207Response, setHas207Response] = React.useState(false);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const dispatch = useDispatch();
  const response = useSelector(selectUserData);
  const permission = response?.data?.website_permissions?.[0];
  const siteId = permission ? Object.keys(permission)[0] : "";
  const {
    manualReceiptsPayload,
    uploadReceiptsPayload,
    allCompanies,
    allCurrencies,
    loadingModal,
    selectedReceiptDateRange,
    failedReceipts,
    failedReceiptsPayload,
  } = useSelector((state: any) => state.receipts);
  const from_date = selectedReceiptDateRange[0]?.format("YYYY-MM-DD");
  const to_date = selectedReceiptDateRange[1]?.format("YYYY-MM-DD");
  // FME is optional: null is valid; when present, every entry must be a
  // non-negative integer (digits only, no empty/alpha/special/negative values).
  const isFmeValid = (fmeRecords: any) =>
    fmeRecords == null ||
    (Array.isArray(fmeRecords) &&
      fmeRecords.every((v: any) => /^\d+$/.test(String(v))));
  const isValidReceiptDate = (value: any) =>
    value != null && String(value).trim() !== "" && dayjs(value).isValid();
  const isManualFormValid = React.useMemo(() => {
    if (
      !Array.isArray(manualReceiptsPayload) ||
      manualReceiptsPayload.length === 0
    ) {
      return false;
    }

    return manualReceiptsPayload.every((r: any) => {
      return (
        r.receipt_id &&
        r.receipt_id.trim() !== "" &&
        isValidReceiptDate(r.receipt_date) &&
        r.receipt_amount !== null &&
        !Number.isNaN(r.receipt_amount) &&
        r.receipt_amount > 0 &&
        r.receipt_currency_shared_uid &&
        r.company_shared_uid &&
        isFmeValid(r.fme_records)
        // r.receipt_status
      );
    });
  }, [manualReceiptsPayload]);
  const hasDuplicateReceiptNumbers = React.useMemo(() => {
    const values = manualReceiptsPayload
      .map((r: any) => r.receipt_id)
      .filter(Boolean);

    return new Set(values).size !== values.length;
  }, [manualReceiptsPayload]);

  const isUploadDataValid = React.useMemo(() => {
    if (
      !Array.isArray(uploadReceiptsPayload) ||
      uploadReceiptsPayload.length === 0
    ) {
      return false;
    }

    return uploadReceiptsPayload.every((r: any) => {
      return (
        r.receipt_id &&
        r.receipt_id.trim() !== "" &&
        isValidReceiptDate(r.receipt_date) &&
        r.receipt_amount !== null &&
        !Number.isNaN(r.receipt_amount) &&
        r.receipt_amount > 0 &&
        r.receipt_currency_shared_uid &&
        r.company_shared_uid &&
        isFmeValid(r.fme_records)
      );
    });
  }, [uploadReceiptsPayload]);

  const baseSteps = ["Method Selection", "Input", "Preview", "Upload Summary"];

  // Dynamically add "Failed to Upload" step only when has207Response is true
  const steps = React.useMemo(() => {
    return has207Response ? [...baseSteps, "Failed to Upload"] : baseSteps;
    // eslint-disable-next-line
  }, [has207Response]);

  const resetModalState = () => {
    setActiveStep(0);
    setMethod("uploadFile");
    setUploadedFiles([]);
    setParsedRows([]);
    setSaveSuccess(false);
    setHas207Response(false);
  };

  const [activeStep, setActiveStep] = React.useState(0);
  const [method, setMethod] = React.useState<"uploadFile" | "manual_input">(
    "uploadFile",
  );

  const failedReceiptsWithDetails = failedReceipts
    .filter((receipt: any) => receipt.created === false)
    .map((failedReceipt: any) => {
      const payloadData = failedReceiptsPayload?.receipts?.find(
        (payload: any) => payload.receipt_id === failedReceipt.receipt_id,
      );
      return {
        ...payloadData,
        ...failedReceipt,
      };
    });

  const convertToCSV = (data: any) => {
    if (!Array.isArray(data) || data.length === 0) return "";
    const keys = [
      "receipt_id",
      "receipt_date",
      "receipt_amount",
      "currency_code",
      "finance_company_name",
      "created",
      "reason",
    ];
    const header = keys.join(",");
    const rows = data.map((receipt) =>
      keys
        .map((key) => {
          let value: string | number | null | undefined;

          if (key === "currency_code") {
            value =
              allCurrencies?.find(
                (currency: any) =>
                  currency?.shared_uid === receipt?.receipt_currency_shared_uid,
              )?.currency_code ?? "";
          } else if (key === "finance_company_name") {
            value =
              allCompanies?.find(
                (company: any) =>
                  company?.shared_uid === receipt?.company_shared_uid,
              )?.finance_company_name ?? "";
          } else {
            value = receipt?.[key];
          }

          const stringValue =
            value === null || value === undefined ? "" : String(value);

          const escaped = stringValue.replace(/"/g, '""');

          return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
        })
        .join(","),
    );
    return [header, ...rows].join("\r\n");
  };

  const triggerBrowserDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const fetchAndDownload = async () => {
    try {
      setIsDownloading(true);
      await new Promise((resolve) => setTimeout(resolve, 2000));
      const timestamp = dayjs().format("MMM D, YYYY, h:mm A");
      const rawTableData = failedReceiptsWithDetails;
      let blob: Blob;
      let fileNameBase = `FailedReceipts_${timestamp}`;
      const csv = Array.isArray(rawTableData) ? convertToCSV(rawTableData) : "";
      blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const filename = `${fileNameBase}.csv`;
      triggerBrowserDownload(blob, filename);
    } catch (error: any) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: error?.parsedText || "Something went wrong from the getdata",
          type: "error",
        }),
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const handleNext = async () => {
    if (activeStep === 2) {
      // Review & Save step (Preview is always index 2)
      await handleSave();
    } else if (activeStep === 3 && has207Response) {
      // If we're at Upload Summary with 207 response, go to Failed to Upload step
      setActiveStep(4);
    } else if (activeStep === 3 && saveSuccess) {
      // Success step - close modal
      dispatch(setManualReceiptsPayload([]));
      dispatch(setUploadReceiptsPayload([]));
      resetModalState();
      handleClose();
    } else if (activeStep === 4) {
      // Failed to Upload step - close modal
      dispatch(setManualReceiptsPayload([]));
      dispatch(setUploadReceiptsPayload([]));
      resetModalState();
      handleClose();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === 1) {
      dispatch(setManualReceiptsPayload([]));
      dispatch(setUploadReceiptsPayload([]));
      dispatch(resetReceiptsForm());
      setUploadedFiles([]);
      setParsedRows([]);
    }
    if (activeStep > 0 && activeStep < 3) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleSave = async () => {
    const basePayload =
      method === "manual_input" ? manualReceiptsPayload : uploadReceiptsPayload;
    const payloadToSend = {
      receipts: basePayload,
    };
    setIsSubmitting(true);
    try {
      const response = await ReceiptsEndPointLogic.createReceipt(
        oauthTokenHeader,
        payloadToSend,
      );
      if (response && [201].includes(response.status)) {
        setSaveSuccess(true);
        setHas207Response(false);
        setActiveStep(3); // Move to Upload Summary step
        await ReceiptsEndPointLogic.getAllReceiptData(
          oauthTokenHeader,
          from_date,
          to_date,
        );
        await ReceiptsEndPointLogic.getPairingRate(
          oauthTokenHeader,
          ParingRateQueryID,
          from_date,
          to_date,
          siteId,
        );
        store.dispatch(
          setDisplayErrorSnackBar({
            message: "Receipt created successfully",
            type: "success",
          }),
        );
      } else if (response && [207].includes(response.status)) {
        setSaveSuccess(false);
        setHas207Response(true);
        setActiveStep(3); // Move to Upload Summary step
        await ReceiptsEndPointLogic.getAllReceiptData(
          oauthTokenHeader,
          from_date,
          to_date,
        );
        await ReceiptsEndPointLogic.getPairingRate(
          oauthTokenHeader,
          ParingRateQueryID,
          from_date,
          to_date,
          siteId,
        );
      }
    } catch (err) {
      console.error("Failed to save data", err);
      return;
    } finally {
      setIsSubmitting(false);
    }
  };

  const reviewRows = React.useMemo(() => {
    const source =
      method === "manual_input" ? manualReceiptsPayload : uploadReceiptsPayload;
    if (!Array.isArray(source) || source.length === 0) return [];

    return source.map((r: any) => {
      const company = allCompanies?.find(
        (c: any) => c.shared_uid === r.company_shared_uid,
      );
      const companyName =
        company?.company_name ?? r.finance_company_name ?? "-";
      const financeCompanyId =
        company?.finance_company_id ?? r.finance_company_id ?? "";
      const currency = allCurrencies?.find(
        (cur: any) => cur.shared_uid === r.receipt_currency_shared_uid,
      );

      const currencySymbol = currency?.currency_symbol ?? "";

      const fmeRecords = Array.isArray(r.fme_records)
        ? r.fme_records.filter((v: any) => v !== null && v !== "")
        : [];

      return {
        company: companyName,
        receipt_number: r.receipt_id ?? "",
        receipt_amount:
          r.receipt_amount !== undefined && r.receipt_amount !== null
            ? `${r.receipt_amount.toString()}${
                currencySymbol ? ` ${currencySymbol}` : ""
              }`
            : "",
        finance_company_id: financeCompanyId,
        fme_record_id: fmeRecords.length > 0 ? fmeRecords.join(", ") : "",
      };
    });
  }, [
    method,
    manualReceiptsPayload,
    uploadReceiptsPayload,
    allCompanies,
    allCurrencies,
  ]);

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <AddReceiptsMethodSelection method={method} setMethod={setMethod} />
        );

      case 1:
        return method === "uploadFile" ? (
          <AddReceiptsUploadFile
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            parsedRows={parsedRows}
            setParsedRows={setParsedRows}
          />
        ) : (
          <AddReceiptsManualInput />
        );

      case 2:
        return (
          <AddReceiptsReviewTable
            tableHeadings={tableHeadings}
            rows={reviewRows}
          />
        );

      case 3:
        return saveSuccess ? (
          <Alert
            severity="success"
            icon={<CheckCircleOutlineIcon fontSize="inherit" />}
          >
            <Typography fontWeight={600}>
              All records were uploaded successfully
            </Typography>
          </Alert>
        ) : (
          <>
            <AddReceiptFailedReport />
          </>
        );

      case 4:
        return (
          <FailedReceipts
            failedReceiptsWithDetails={failedReceiptsWithDetails}
          />
        );

      default:
        return "Unknown Step";
    }
  };

  const getButtonLabel = () => {
    if (activeStep === 4) {
      return "Close";
    }
    if (activeStep === 3) {
      if (has207Response) {
        return "Failed Uploads";
      }
      return "Done";
    }
    if (activeStep === 2) {
      return "Add receipt";
    }
    return "Next";
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (
            reason !== "backdropClick" &&
            activeStep !== 3 &&
            activeStep !== 4
          ) {
            handleClose();
          }
        }}
        PaperProps={{ sx: { width: "900px", maxWidth: "900px" } }}
      >
        <Paper sx={{ padding: "16px 24px 20px 24px" }}>
          <DialogTitle sx={{ padding: "0px 0px 16px 0px" }}>
            {activeStep === 4 ? "Failed to Upload" : "Add New Receipt"}
          </DialogTitle>

          {loadingModal ? (
            <ModalSkeleton />
          ) : (
            <Box sx={{ width: "100%", mt: 2 }}>
              <Stepper
                sx={{
                  "& .MuiStepIcon-root": {
                    color: "rgba(0,0,0,0.3)", // default inactive
                  },
                  "& .MuiStepIcon-root.Mui-active": {
                    color: "secondary.main",
                  },
                  "& .MuiStepIcon-root.Mui-completed": {
                    color: "secondary.main",
                  },
                }}
                activeStep={activeStep}
              >
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              {/* Step Content */}
              <Box
                sx={{
                  my: 4,
                  px: 1,
                }}
              >
                {renderStepContent(activeStep)}
              </Box>

              {/* Buttons */}
            </Box>
          )}
          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
            {activeStep !== 3 && activeStep !== 4 && (
              <>
                <Button
                  disabled={activeStep === 0 || isSubmitting}
                  onClick={handleBack}
                  color="secondary"
                  sx={{ mr: 1 }}
                >
                  Back
                </Button>

                <Button
                  color="secondary"
                  variant="outlined"
                  disabled={isSubmitting}
                  onClick={() => {
                    resetModalState();
                    handleClose();
                  }}
                  sx={{ mr: 1 }}
                >
                  Cancel
                </Button>
              </>
            )}
            {activeStep !== 4 && (
              <Button
                key={activeStep}
                variant="contained"
                color="secondary"
                onClick={handleNext}
                {...(activeStep === 2 && {
                  "data-tracker": method === "uploadFile" ? 583 : 584,
                })}
                disabled={
                  isSubmitting ||
                  loadingModal ||
                  (activeStep === 1 &&
                    method === "manual_input" &&
                    (!isManualFormValid || hasDuplicateReceiptNumbers)) ||
                  (activeStep === 1 &&
                    method === "uploadFile" &&
                    !isUploadDataValid)
                }
                endIcon={
                  isSubmitting ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (activeStep === 3 && !has207Response) ||
                    activeStep === 4 ? null : activeStep === 2 ? (
                    <AddIcon sx={{ ml: "0.5" }} />
                  ) : (
                    <ChevronRightIcon sx={{ ml: "0.5" }} />
                  )
                }
              >
                {isSubmitting ? "Saving..." : getButtonLabel()}
              </Button>
            )}
            {activeStep === 4 && (
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <Button
                  color="secondary"
                  disabled={isSubmitting || isDownloading}
                  onClick={() => {
                    handleNext();
                    dispatch(setFailedReceiptStatus(false));
                    dispatch(setFailedReceipts([]));
                    dispatch(setFailedReceiptsPayload([]));
                  }}
                  sx={{ mr: 2 }}
                >
                  Close
                </Button>
                <Button
                  color="secondary"
                  variant="outlined"
                  loading={isDownloading}
                  disabled={isSubmitting || isDownloading}
                  onClick={() => fetchAndDownload()}
                  sx={{ mr: 2 }}
                >
                  {isDownloading ? "Downloading..." : "Download File"}
                </Button>
                <Button
                  color="secondary"
                  variant="contained"
                  disabled={isSubmitting || isDownloading}
                  onClick={() => {
                    // Reset to Input step (step 1) and clear relevant data based on method
                    if (method === "uploadFile") {
                      setUploadedFiles([]);
                      setParsedRows([]);
                      dispatch(setUploadReceiptsPayload([]));
                    } else if (method === "manual_input") {
                      dispatch(setManualReceiptsPayload([]));
                      dispatch(resetReceiptsForm());
                    }
                    setHas207Response(false);
                    setSaveSuccess(false);
                    setActiveStep(1);
                  }}
                  sx={{ mr: 2 }}
                >
                  Retry
                </Button>
              </Box>
            )}
          </Box>
        </Paper>
      </Dialog>
    </>
  );
};

export default AddReceiptsModal;
